import { Link } from 'react-router-dom'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { Tooltip } from '../../Tooltip/Tooltip'
import React, { ElementType, useMemo } from 'react'
import './IntegrationCard.scss'

const WEBEX_ADMIN_AUTHORIZE_DOC_LINK = import.meta.env.REACT_APP_WEBEX_ADMIN_AUTHORIZE_DOC_LINK!

const MICROSOFT_ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_MICROSOFT_ADMIN_CONSENT_LINK!

const HOLISTIPLAN_INFO_LINK = 'https://help.zocks.io/en/articles/12400657-holistiplan-tax-planning-integration'

const SALESFORCE_ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_SALESFORCE_ADMIN_CONSENT_LINK!

const customHeadline = (integrationServiceId: IntegrationServiceId): React.ReactNode | string | undefined => {
  switch (integrationServiceId) {
    case IntegrationServiceId.PHONE:
      return (
        <div>
          Link your phone numbers. If you would like to learn more, you can read our full{' '}
          <a
            target='_blank'
            href='https://help.zocks.io/en/articles/9969795-how-to-connect-zocks-to-your-phone-calls'
            rel='noreferrer'
            className='integration-card-description-link button-link-medium'
            onClick={(e) => e.stopPropagation()}
          >
            Help&nbsp;article.
          </a>
        </div>
      )
    case IntegrationServiceId.MEET:
    case IntegrationServiceId.TEAMS:
      return (
        <div>
          Connect your calendar or{' '}
          <Link to='/workspace/upcoming' className='integration-card-description-link' onClick={(e) => e.stopPropagation()} target='_blank'>
            invite
          </Link>{' '}
          the Assistant to your meetings.
        </div>
      )
    case IntegrationServiceId.GOOGLE_CALENDAR:
      return 'Connect your Google Calendar and control when your Zocks AI Notetaker joins your meetings.'
    case IntegrationServiceId.OUTLOOK_CALENDAR:
      return 'Connect your Outlook Calendar and control when your Zocks AI Notetaker joins your meetings.'
    case IntegrationServiceId.EXCHANGE_CALENDAR:
      return 'Connect your Exchange Calendar and control when your Zocks AI Notetaker joins your meetings.'
    case IntegrationServiceId.GMAIL:
      return 'Connect your Gmail account to send and sync your emails.'
    case IntegrationServiceId.OUTLOOK_MAIL:
      return 'Connect your Outlook account to send and sync your emails.'
    case IntegrationServiceId.E_MONEY:
      return 'Connect your eMoney account to Zocks to keep your planning data up-to-date.'
    case IntegrationServiceId.RIGHT_CAPITAL:
      return 'Connect your RightCapital account to Zocks to keep your planning data up-to-date.'
    case IntegrationServiceId.ORION:
      return 'Connect your Orion account to Zocks to get real-time porfolio data for your clients.'
    case IntegrationServiceId.HOLISTIPLAN:
      return 'Connect your Holistiplan account to Zocks to get real-time tax analysis data for your clients.'
    default:
      return undefined
  }
}

const customDescription = (integrationServiceType: IntegrationServiceId): React.ReactNode | string | undefined => {
  switch (integrationServiceType) {
    case IntegrationServiceId.WEBEX:
      return (
        <div>
          An admin may need to approve Zocks in Webex Control Hub. For more info, click{' '}
          <Link target='_blank' className='integration-card-description-link' to={WEBEX_ADMIN_AUTHORIZE_DOC_LINK} onClick={(e) => e.stopPropagation()}>
            here
          </Link>{' '}
          .
        </div>
      )
    case IntegrationServiceId.OUTLOOK_MAIL:
    case IntegrationServiceId.DYNAMICS:
    case IntegrationServiceId.ENGAGE:
      return (
        <div>
          It might be needed for your admin to approve the integration. For more info, click{' '}
          <Link className='integration-card-description-link' to={MICROSOFT_ADMIN_CONSENT_LINK} onClick={(e) => e.stopPropagation()} target='_blank'>
            here
          </Link>{' '}
          .
        </div>
      )
    case IntegrationServiceId.HOLISTIPLAN:
      return (
        <div>
          Your Firm Admin must enable Zocks in Holistiplan under Settings {'>'} Integrations. For more info, click{' '}
          <Link className='integration-card-description-link' to={HOLISTIPLAN_INFO_LINK} onClick={(e) => e.stopPropagation()} target='_blank'>
            here
          </Link>{' '}
          .
        </div>
      )
    case IntegrationServiceId.SALESFORCE:
    case IntegrationServiceId.PRACTIFI:
    case IntegrationServiceId.XLR8:
    case IntegrationServiceId.ELEMENTS:
    case IntegrationServiceId.BLACK_DIAMOND:
      return (
        <div>
          Your Salesforce Admin must enable the Zocks app in Salesforce. For more info, click{' '}
          <Link className='integration-card-description-link' to={SALESFORCE_ADMIN_CONSENT_LINK} onClick={(e) => e.stopPropagation()} target='_blank'>
            here
          </Link>{' '}
          . &nbsp;&nbsp;
          <Tooltip
            className='zocks-text-gray-500'
            placement='right'
            title='Go to Apps > Connected Apps > Connected Apps OAuth Usage and click Install next to Zocks before connecting.'
          >
            <i className='bi bi-info-circle' />
          </Tooltip>
        </div>
      )
    default:
      return undefined
  }
}

export interface IntegrationCardDescriptionProps {
  serviceId: IntegrationServiceId
  headerAs?: ElementType
  description?: React.ReactNode | string
}

export const IntegrationCardDescription: React.FC<IntegrationCardDescriptionProps> = ({ serviceId, headerAs: Header = 'h6', description }) => {
  const connectorDisplayName = getIntegrationDisplayName(serviceId)
  const integrationCardHeadline = useMemo(() => {
    return description || customHeadline(serviceId) || `Connect your Zocks account to ${connectorDisplayName}.`
  }, [serviceId, description, connectorDisplayName])

  const integrationCardDescription = useMemo(() => customDescription(serviceId), [serviceId])

  return (
    <div className='h-auto'>
      <Header className='zocks-text-gray-900 integration-card-description-label'>{connectorDisplayName}</Header>
      <div className='zocks-text-gray-600'> {integrationCardHeadline}</div>
      {integrationCardDescription && <div className='mt-2 zocks-text-gray-600'>{integrationCardDescription}</div>}
    </div>
  )
}
